import type { ProviderFieldError } from '@/domain/providerConfig';

/**
 * `unfinished`: the only problem is a SCADA row the user is still filling in, not yet marked.
 * `requiredMissing`: required boxes are still empty, held back from red until touched.
 */
export type SaveBlocker =
    | 'nothingChanged'
    | 'invalid'
    | 'unfinished'
    | 'requiredMissing'
    | 'testRequired';

/** The client code a row just added to a SCADA list gets before it is filled in. */
const UNFINISHED_ROW_CODE = 'scadaItems';

export interface SaveBlockerInput {
    dirty: boolean;
    /** Paths whose field's own rule refuses the value (ruleErrorPaths). */
    ruleErrorPaths: readonly string[];
    /** The client errors the card marks (splitQuietRequired's `shown`). */
    clientErrors: readonly ProviderFieldError[];
    /** Required boxes still empty that are not marked yet. */
    requiredMissing: boolean;
    /** Fields whose editor holds its marks back (HeldBackFields). */
    heldBack: ReadonlySet<string>;
    testMissing: boolean;
}

/**
 * What keeps Save disabled, or null. A held field's own rule, and the row-format code the
 * client rules give the same blank row, read as unfinished, so the card does not point at
 * marks that are not there. Any other client code at a held field, such as an id another pen
 * uses, is a real problem and reads as invalid.
 */
export function saveBlocker({
    dirty,
    ruleErrorPaths,
    clientErrors,
    requiredMissing,
    heldBack,
    testMissing,
}: SaveBlockerInput): SaveBlocker | null {
    if (!dirty) return 'nothingChanged';
    const invalid =
        ruleErrorPaths.some((path) => !heldBack.has(path)) ||
        clientErrors.some(
            (error) => !heldBack.has(error.path) || error.code !== UNFINISHED_ROW_CODE
        );
    if (invalid) return 'invalid';
    if (ruleErrorPaths.length > 0 || clientErrors.length > 0) return 'unfinished';
    if (requiredMissing) return 'requiredMissing';
    return testMissing ? 'testRequired' : null;
}
